import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTrash, 
  FaSpinner, 
  FaEnvelope, 
  FaUser, 
  FaCalendar,
  FaEye,
  FaTimes
} from 'react-icons/fa';
import { messagesAPI } from '../../utils/api.js';

const MessagesViewer = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setIsLoading(true);
      const response = await messagesAPI.getAll();
      if (response.success) {
        setMessages(response.data || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessage('Error loading messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const response = await messagesAPI.delete(id);
      if (response.success) {
        setMessages(messages.filter(msg => msg.id !== id));
        setMessage('Message deleted successfully!');
        
        // Close modal if the deleted message was selected
        if (selectedMessage?.id === id) {
          setShowModal(false);
          setSelectedMessage(null);
        }
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      setMessage('Error deleting message');
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMessage(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Collaborate Messages</h3>
        <div className="text-sm text-gray-400">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </div>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Messages List */}
      <div className="space-y-3">
        {messages.map(msg => (
          <motion.div
            key={msg.id}
            layout
            className="bg-gray-700 rounded-lg p-4 hover:bg-gray-650 transition-colors cursor-pointer"
            onClick={() => openMessage(msg)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <FaUser className="text-primary" />
                  <span className="font-medium text-white">{msg.name}</span>
                  <FaEnvelope className="text-gray-400" />
                  <span className="text-gray-300">{msg.email}</span>
                </div>
                
                <p className="text-gray-300 line-clamp-2 mb-2">
                  {msg.message}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <FaCalendar />
                  <span>{formatDate(msg.created_at)}</span>
                </div>
              </div>
              
              <div className="flex gap-2 ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openMessage(msg);
                  }}
                  className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded"
                  title="View Details"
                >
                  <FaEye />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMessage(msg.id);
                  }}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                  title="Delete Message"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {messages.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <FaEnvelope className="mx-auto text-6xl text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">No messages yet</p>
          <p className="text-gray-500 text-sm mt-2">
            Messages from the collaborate form will appear here
          </p>
        </div>
      )}

      {/* Message Detail Modal */}
      <AnimatePresence>
        {showModal && selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-semibold text-white">Message Details</h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Name
                    </label>
                    <div className="px-3 py-2 bg-gray-700 rounded text-white">
                      {selectedMessage.name}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email
                    </label>
                    <div className="px-3 py-2 bg-gray-700 rounded text-white">
                      {selectedMessage.email}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Date
                  </label>
                  <div className="px-3 py-2 bg-gray-700 rounded text-white">
                    {formatDate(selectedMessage.created_at)}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Message
                  </label>
                  <div className="px-3 py-2 bg-gray-700 rounded text-white whitespace-pre-wrap min-h-[100px]">
                    {selectedMessage.message}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: Your message from Dhiraj's Portfolio`}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                  >
                    Reply via Email
                  </a>
                  <button
                    onClick={() => handleDeleteMessage(selectedMessage.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete Message
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MessagesViewer; 